import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, Field } from "../components/Shared";
import { ProofImage } from "../components/ProofImage";
import { LinkifiedText } from "../components/LinkifiedText";
import { Textarea } from "../components/ui/textarea";
import { ImageUpload } from "../components/ImageUpload";
import { DateField } from "../components/DateField";
import { StreakBadge } from "../components/StreakBadge";
import { StreakBoard } from "../components/StreakBoard";
import { WhatsAppReminder } from "../components/WhatsAppReminder";
import { Switch } from "../components/ui/switch";
import { FOOD } from "../constants/testIds";
import { Sparkles, Trash2, MessageSquare, Loader2, EyeOff, Eye, Trophy, MapPin, Clock } from "lucide-react";

const MEALS = ["SARAPAN", "MAKAN_SIANG", "MAKAN_MALAM", "SNACK"];
const REACTIONS = ["👍", "👎", "❌", "❤️", "😍", "🎉"];
const VERDICT_STYLE = {
  BAIK: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  CUKUP: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  PERLU_PERBAIKAN: "border-red-500/30 bg-red-500/10 text-red-300",
};

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) + " WIB" : null);

export default function FoodJournal() {
  const { user } = useAuth();
  const { t } = useLang();
  const role = user?.role;
  const isMember = role === "MEMBER";
  const isAdmin = role === "MASTER_ADMIN" || role === "CO_ADMIN";
  const [access, setAccess] = useState(null);
  const [tab, setTab] = useState("FEED");
  const [entries, setEntries] = useState([]);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({ date: "", batch_id: "", user_id: "", role: "", post_type: "" });
  const [composer, setComposer] = useState(false);

  useEffect(() => {
    api.get("/food-journal/access").then(({ data }) => setAccess(data.data)).catch((e) => toast.error(extractError(e).message));
  }, []);

  const load = useCallback(async () => {
    if (!access?.allowed) return;
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    const q = qs ? `?${qs}` : "";
    try {
      if (tab === "MINE") setEntries((await api.get(`/food-journal/me${filters.date ? `?date=${filters.date}` : ""}`)).data.data);
      else if (tab === "FEED") setEntries((await api.get(`/food-journal/wall${q}`)).data.data.entries);
      else setEntries([]);
    } catch (e) { toast.error(extractError(e).message); }
  }, [access, tab, filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isMember || !access?.allowed) return;
    api.get("/food-journal/members").then(({ data }) => setMembers(data.data)).catch(() => {});
  }, [isMember, access]);

  if (access && !access.allowed) {
    return (
      <div data-testid={FOOD.page} className="space-y-6">
        <PageHeader eyebrow={t("food.eyebrow")} title={t("food.title")} />
        <Card data-testid="food-journal-locked" className="bg-[#161923] border-amber-500/25">
          <CardContent className="p-6 text-sm text-amber-200">{access.reason || t("food.locked")}</CardContent>
        </Card>
      </div>
    );
  }

  const tabs = [["FEED", t("food.tabFeed")], ["MINE", t("food.tabMine")],
    ...(isMember ? [] : [["COMPLIANCE", t("food.tabCompliance")]]), ["BOARD", t("food.leaderboard")]];

  return (
    <div data-testid={FOOD.page} className="space-y-6">
      <PageHeader eyebrow={t("food.eyebrow")} title={t("food.title")} subtitle={isMember ? t("food.subtitleMember") : t("food.subtitleCoach")}>
        <Button data-testid="food-new-post-btn" onClick={() => { setComposer((v) => !v); setTab("MINE"); }}
          className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-11 font-semibold">{t("food.newPost")}</Button>
      </PageHeader>

      {isMember && <StreakBadge />}

      <div className="flex flex-wrap gap-2">
        {tabs.map(([key, label]) => (
          <button key={key} data-testid={`food-tab-${key}`} onClick={() => setTab(key)}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition ${tab === key ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-white" : "border-white/10 text-slate-400 hover:bg-white/5"}`}>
            {label}
          </button>
        ))}
      </div>

      {(tab === "FEED" || tab === "MINE") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <DateField label={t("food.filterDate")} testId={FOOD.dateFilter} value={filters.date} onChange={(v) => setFilters({ ...filters, date: v })} />
          {tab === "FEED" && (
            <>
              <Select label={t("food.filterBatch")} testId="food-filter-batch" value={filters.batch_id} onChange={(v) => setFilters({ ...filters, batch_id: v })}
                options={[["", t("food.all")], ...(access?.batches || []).map((b) => [b.batch_id, `${b.code}`])]} />
              <Select label={t("food.filterRole")} testId="food-filter-role" value={filters.role} onChange={(v) => setFilters({ ...filters, role: v })}
                options={[["", t("food.all")], ["MEMBER", "Member"], ["COACH", "Coach"]]} />
              <Select label={t("food.filterType")} testId="food-filter-type" value={filters.post_type} onChange={(v) => setFilters({ ...filters, post_type: v })}
                options={[["", t("food.all")], ["FOOD", t("food.postFood")], ["EDUCATION", t("food.postEducation")]]} />
              {!isMember && (
                <Select label={t("food.filterOwner")} testId={FOOD.memberSelect} value={filters.user_id} onChange={(v) => setFilters({ ...filters, user_id: v })}
                  options={[["", t("food.all")], ...members.map((m) => [m.user_id, m.full_name || m.email])]} />
              )}
            </>
          )}
        </div>
      )}

      {tab === "MINE" && composer && <Composer role={role} onDone={() => { setComposer(false); load(); }} />}
      {tab === "MINE" && <DailySummary date={filters.date} />}
      {tab === "BOARD" && <><Leaderboard batches={access?.batches || []} canPick={!isMember} /><StreakBoard canPickBatch={!isMember} /></>}
      {tab === "COMPLIANCE" && <Compliance date={filters.date} />}

      {(tab === "FEED" || tab === "MINE") && (entries.length === 0 ? (
        <EmptyState testId={FOOD.empty} text={t("food.empty")} />
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <PostCard key={e.entry_id} entry={e} isAdmin={isAdmin} canModerate={role === "COACH" || isAdmin}
              isOwner={e.user_id === user?.user_id} myName={user?.profile?.full_name || ""}
              onChanged={load}
              onReplace={(u) => setEntries((prev) => prev.map((x) => (x.entry_id === u.entry_id ? u : x)))}
              onUpdate={(id, fn) => setEntries((prev) => prev.map((x) => (x.entry_id === id ? fn(x) : x)))}
              onRemove={(id) => setEntries((prev) => prev.filter((x) => x.entry_id !== id))} />
          ))}
        </div>
      ))}
    </div>
  );
}

function Select({ label, testId, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <label className="text-slate-300 text-sm">{label}</label>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Composer({ role, onDone }) {
  const { t } = useLang();
  const canEducate = role === "COACH" || role === "MASTER_ADMIN" || role === "CO_ADMIN";
  const [form, setForm] = useState({ post_type: "FOOD", meal_type: "SARAPAN", note: "" });
  const [fileId, setFileId] = useState(null);
  const [coords, setCoords] = useState(null);
  const [busy, setBusy] = useState(false);

  const askLocation = () => {
    if (!navigator.geolocation) return toast.error(t("food.locationDenied"));
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ latitude: p.coords.latitude, longitude: p.coords.longitude }); toast.success(t("food.location")); },
      () => toast.message(t("food.locationDenied")),
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.post_type === "FOOD" && !fileId) return toast.error(t("food.photoRequired"));
    setBusy(true);
    try {
      await api.post("/food-journal", {
        post_type: form.post_type,
        meal_type: form.post_type === "FOOD" ? form.meal_type : null,
        photo_file_id: fileId, note: form.note || null, ...(coords || {}),
      });
      toast.success(t("food.saved"));
      setForm({ post_type: form.post_type, meal_type: "SARAPAN", note: "" });
      setFileId(null); setCoords(null);
      onDone();
    } catch (err) { toast.error(extractError(err).message); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid={FOOD.form} className="bg-[#10121B] border-[#FF3B30]/25">
      <CardContent className="p-5">
        <form onSubmit={submit} className="space-y-4">
          {canEducate && (
            <div className="flex gap-2">
              {[["FOOD", t("food.postFood")], ["EDUCATION", t("food.postEducation")]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`food-post-type-${v}`} onClick={() => setForm({ ...form, post_type: v })}
                  className={`rounded-full border px-3 py-1.5 text-xs ${form.post_type === v ? "border-[#FF3B30]/60 bg-[#FF3B30]/10 text-white" : "border-white/10 text-slate-400"}`}>{l}</button>
              ))}
            </div>
          )}
          {form.post_type === "FOOD" && (
            <div className="space-y-1.5">
              <label className="text-slate-300 text-sm">{t("food.mealType")}</label>
              <select data-testid={FOOD.mealSelect} value={form.meal_type} onChange={(ev) => setForm({ ...form, meal_type: ev.target.value })}
                className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
                {MEALS.map((m) => <option key={m} value={m}>{t(`food.meal_${m}`)}</option>)}
              </select>
              {form.meal_type === "MAKAN_SIANG" && <p className="text-xs text-emerald-300">{t("food.auditHint")}</p>}
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("food.caption")}</label>
            <Textarea data-testid={FOOD.noteInput} value={form.note} maxLength={2000}
              onChange={(ev) => setForm({ ...form, note: ev.target.value })}
              placeholder={t("food.captionPlaceholder")}
              className="min-h-[140px] resize-y bg-[#1C202E] border-white/10 text-white leading-relaxed" />
            <div data-testid="food-caption-counter" className="text-right text-[11px] text-slate-500">{form.note.length} / 2000</div>
          </div>
          <ImageUpload endpoint="/uploads/food-photo" testId={FOOD.photoInput} label={t("food.photo")} compress={form.post_type === "EDUCATION" ? "EDU" : "FOOD"} thumbnail onUploaded={setFileId} />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button type="button" data-testid="food-share-location" onClick={askLocation}
              className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:bg-white/5 inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {coords ? t("food.location") : t("food.shareLocation")}
            </button>
            <Button data-testid={FOOD.saveBtn} type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("food.saving")}</> : t("food.save")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PostCard({ entry, isAdmin, canModerate, isOwner, myName, onChanged, onReplace, onUpdate, onRemove }) {
  const { t } = useLang();
  const [comment, setComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const audit = entry.ai_audit;

  // Moderation / rare actions keep a simple guarded call.
  const call = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(extractError(e).message); } finally { setBusy(false); } };

  const computeOptimisticReaction = (cur, emoji) => {
    const counts = { ...(cur.reactions || {}) };
    const prev = cur.my_reaction;
    let my = prev;
    let total = cur.reaction_total || 0;
    if (prev === emoji) {
      counts[emoji] = Math.max(0, (counts[emoji] || 1) - 1);
      if (!counts[emoji]) delete counts[emoji];
      my = null; total -= 1;
    } else {
      if (prev) { counts[prev] = Math.max(0, (counts[prev] || 1) - 1); if (!counts[prev]) delete counts[prev]; }
      else total += 1;
      counts[emoji] = (counts[emoji] || 0) + 1;
      my = emoji;
    }
    return { ...cur, reactions: counts, my_reaction: my, reaction_total: total };
  };

  const react = (emoji) => {
    const snapshot = entry;
    onUpdate(entry.entry_id, (cur) => computeOptimisticReaction(cur, emoji)); // instant feedback off latest state
    (async () => {
      try {
        const { data } = await api.post(`/food-journal/${entry.entry_id}/reactions`, { emoji });
        onReplace(data.data); // reconcile with server truth
      } catch (e) { onReplace(snapshot); toast.error(extractError(e).message); }
    })();
  };

  const send = (e) => {
    e.preventDefault();
    const body = comment.trim();
    if (body.length < 2 || sending) return;
    const tempId = `tmp-${Date.now()}`;
    const pending = { comment_id: tempId, user_id: "me", role: entry.author_role, author_name: myName || "Anda", body, parent_comment_id: replyTo, pending: true };
    const savedReply = replyTo;
    // optimistic add off the LATEST post state (avoids clobbering interleaved reactions)
    onUpdate(entry.entry_id, (cur) => (savedReply
      ? { ...cur, replies: { ...(cur.replies || {}), [savedReply]: [...((cur.replies || {})[savedReply] || []), pending] }, comment_total: (cur.comment_total || 0) + 1 }
      : { ...cur, comments: [...(cur.comments || []), pending], comment_total: (cur.comment_total || 0) + 1 }));
    const savedComment = comment;
    setComment(""); setReplyTo(null); setSending(true);
    (async () => {
      try {
        const { data } = await api.post(`/food-journal/${entry.entry_id}/comments`, { body, parent_comment_id: savedReply });
        onReplace(data.data); // server-rendered entry replaces the pending state
      } catch (err) {
        // remove only the pending comment, keeping any interleaved reactions intact
        onUpdate(entry.entry_id, (cur) => ({
          ...cur,
          comments: (cur.comments || []).filter((c) => c.comment_id !== tempId),
          replies: Object.fromEntries(Object.entries(cur.replies || {}).map(([pid, list]) => [pid, list.filter((c) => c.comment_id !== tempId)])),
          comment_total: Math.max(0, (cur.comment_total || 1) - 1),
        }));
        setComment(savedComment); setReplyTo(savedReply);
        toast.error(extractError(err).message);
      } finally { setSending(false); }
    })();
  };

  return (
    <Card data-testid={`food-entry-${entry.entry_id}`} className={`bg-[#161923] border-white/5 ${entry.is_hidden ? "opacity-60" : ""}`}>
      <CardContent className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 shrink-0 rounded-full bg-white/10 flex items-center justify-center font-bold text-white">
            {(entry.member_name || "?").charAt(0)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-white truncate">{entry.member_name || "—"}</span>
              <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">{entry.author_role || "MEMBER"}</span>
              {entry.post_type === "EDUCATION" && <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-sky-300">{t("food.postEducation")}</span>}
              {entry.meal_type && <span className="text-xs text-slate-400">{t(`food.meal_${entry.meal_type}`)}</span>}
              {entry.is_hidden && <span data-testid={`food-hidden-${entry.entry_id}`} className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-300">{t("food.hidden")}</span>}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t("food.uploadedAt")}: {fmtTime(entry.uploaded_at || entry.created_at)}</span>
              <span data-testid={`food-captured-${entry.entry_id}`}>{entry.captured_at ? `${t("food.capturedAt")}: ${fmtTime(entry.captured_at)}` : t("food.captureUnknown")}</span>
              {entry.batch_code && <span>· {entry.batch_code}</span>}
              {(entry.location_label || entry.latitude) && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {entry.location_label || t("food.location")}</span>
              )}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            {canModerate && (
              <Button data-testid={`food-hide-${entry.entry_id}`} variant="ghost" disabled={busy}
                onClick={() => call(async () => { await api.post(`/food-journal/${entry.entry_id}/hide?hidden=${!entry.is_hidden}`); onChanged(); })}
                className="text-slate-400 hover:bg-white/5 h-8 px-2">
                {entry.is_hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
            )}
            {isOwner && (
              <Button data-testid={`food-delete-${entry.entry_id}`} variant="ghost" disabled={busy}
                onClick={() => { if (window.confirm(t("food.confirmDelete"))) call(async () => { await api.delete(`/food-journal/${entry.entry_id}`); onRemove(entry.entry_id); }); }}
                className="text-red-400 hover:bg-red-500/10 h-8 px-2"><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        </div>

        {entry.note && <LinkifiedText text={entry.note} testId={`food-note-${entry.entry_id}`} className="text-sm text-slate-200" clamp={6} />}
        {entry.photo_file_id && <ProofImage fileId={entry.photo_file_id} thumbFileId={entry.photo_thumb_file_id} natural testId={`food-photo-${entry.entry_id}`} />}

        {audit && (
          <div data-testid={`food-audit-${entry.entry_id}`} className={`rounded-xl border p-3 text-sm ${VERDICT_STYLE[audit.verdict] || "border-white/10 text-slate-300"}`}>
            <div className="flex items-center gap-2 font-semibold flex-wrap">
              <Sparkles className="h-4 w-4" /> {t("food.aiVerdict")}: {t(`food.verdict_${audit.verdict}`)}
              {audit.calories_estimate ? <span className="text-xs font-normal opacity-80">· ± {audit.calories_estimate} kkal</span> : null}
              {audit.cached && <span className="text-[10px] uppercase tracking-wider opacity-70">({t("food.aiCached")})</span>}
            </div>
            {audit.summary && <p className="mt-1.5 opacity-90">{audit.summary}</p>}
            {audit.suggestions?.length > 0 && <ul className="mt-2 space-y-1 text-xs opacity-90">{audit.suggestions.map((s) => <li key={s}>• {s}</li>)}</ul>}
          </div>
        )}
        {isOwner && entry.meal_type === "MAKAN_SIANG" && !audit && (
          <Button data-testid={`food-audit-btn-${entry.entry_id}`} disabled={busy} variant="ghost"
            onClick={() => call(async () => { await api.post(`/food-journal/${entry.entry_id}/ai-audit`); onChanged(); })}
            className="text-emerald-300 hover:bg-emerald-500/10 h-9"><Sparkles className="h-4 w-4 mr-1" /> {t("food.audit")}</Button>
        )}

        <div data-testid={`food-reactions-${entry.entry_id}`} className="flex flex-wrap items-center gap-1.5 border-t border-white/5 pt-3">
          {REACTIONS.map((emoji) => (
            <button key={emoji} type="button" data-testid={`food-react-${entry.entry_id}-${emoji}`}
              onClick={() => react(emoji)}
              className={`rounded-full border px-2.5 py-1 text-sm transition-colors ${entry.my_reaction === emoji ? "border-[#FF3B30]/60 bg-[#FF3B30]/15" : "border-white/10 hover:bg-white/5"}`}>
              {emoji}{entry.reactions?.[emoji] ? <span className="ml-1 text-[11px] text-slate-300">{entry.reactions[emoji]}</span> : null}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {(entry.comments || []).map((c) => (
            <div key={c.comment_id} data-testid={`food-comment-${c.comment_id}`} className={`text-sm ${c.pending ? "opacity-50" : ""}`}>
              <span className="text-white font-medium">{c.author_name || c.role}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500 ml-2">{c.role}</span>
              {c.pending && <span className="text-[10px] text-slate-500 ml-2">{t("food.sending") || "mengirim…"}</span>}
              <p className="text-slate-400">{c.body}</p>
              {!isAdmin && !c.pending && (
                <button type="button" data-testid={`food-reply-btn-${c.comment_id}`} onClick={() => setReplyTo(c.comment_id)} className="text-[11px] text-slate-500 hover:text-white">{t("food.reply")}</button>
              )}
              {(entry.replies?.[c.comment_id] || []).map((r) => (
                <div key={r.comment_id} data-testid={`food-reply-${r.comment_id}`} className={`ml-5 mt-1 border-l border-white/10 pl-3 ${r.pending ? "opacity-50" : ""}`}>
                  <span className="text-white text-xs font-medium">{r.author_name || r.role}</span>
                  <p className="text-slate-400 text-xs">{r.body}</p>
                </div>
              ))}
            </div>
          ))}
          {isAdmin ? (
            <p className="text-xs text-slate-500">{t("food.adminReadOnly")}</p>
          ) : (
            <form onSubmit={send} className="flex gap-2 items-end pt-1">
              <div className="flex-1">
                <Field label={null} testId={`food-comment-input-${entry.entry_id}`} value={comment} required minLength={2}
                  placeholder={replyTo ? t("food.replyPlaceholder") : t("food.commentPlaceholder")} onChange={(ev) => setComment(ev.target.value)} />
              </div>
              <Button data-testid={`food-comment-btn-${entry.entry_id}`} type="submit" disabled={sending} className="bg-white/10 hover:bg-white/15 text-white h-10">
                <MessageSquare className="h-4 w-4 mr-1" /> {replyTo ? t("food.reply") : t("food.comment")}
              </Button>
            </form>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Leaderboard({ batches, canPick }) {
  const { t } = useLang();
  const [batchId, setBatchId] = useState("");
  const [role, setRole] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const qs = new URLSearchParams(Object.entries({ batch_id: batchId, role }).filter(([, v]) => v)).toString();
    api.get(`/food-journal/leaderboard${qs ? `?${qs}` : ""}`).then(({ data }) => setRows(data.data.rows)).catch(() => {});
  }, [batchId, role]);

  return (
    <Card data-testid="journal-leaderboard" className="bg-[#10121B] border-white/5">
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base md:text-lg font-semibold text-white inline-flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-300" /> {t("food.leaderboard")}</h2>
          <div className="flex gap-2">
            {canPick && (
              <select data-testid="leaderboard-batch" value={batchId} onChange={(e) => setBatchId(e.target.value)} className="h-9 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-2">
                <option value="">{t("food.all")}</option>
                {batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{b.code}</option>)}
              </select>
            )}
            <select data-testid="leaderboard-role" value={role} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-2">
              <option value="">{t("food.all")}</option>
              <option value="MEMBER">Member</option>
              <option value="COACH">Coach</option>
            </select>
          </div>
        </div>
        <p data-testid="leaderboard-note" className="text-[11px] text-slate-500 -mt-1">{t("food.pointsPerBatchNote")}</p>
        {rows.length === 0 ? <p className="text-sm text-slate-500">{t("food.empty")}</p> : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.user_id} data-testid={`leaderboard-row-${r.user_id}`} className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${r.is_me ? "border-[#FF3B30]/40 bg-[#FF3B30]/[0.07]" : "border-white/5"}`}>
                <span className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center font-[Outfit] font-extrabold text-sm ${r.rank <= 3 ? "bg-amber-500/15 text-amber-300" : "bg-white/5 text-slate-400"}`}>{r.rank}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-white truncate">{r.full_name || "—"}</div>
                  <div className="text-[11px] text-slate-500">{r.role} · {r.posts} {t("food.posts")}</div>
                </div>
                <div className="shrink-0 font-[Outfit] font-extrabold text-white">{r.points} <span className="text-[10px] font-normal text-slate-500">{t("food.points")}</span></div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Compliance({ date }) {
  const { t } = useLang();
  const { user } = useAuth();
  const isCoach = user?.role === "COACH";
  const [data, setData] = useState(null);
  const [missingOnly, setMissingOnly] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get(`/food-journal/compliance${date ? `?date=${date}` : ""}`).then(({ data }) => setData(data.data)).catch((e) => toast.error(extractError(e).message));
  }, [date]);

  const loadTemplates = useCallback(async () => {
    try { const { data } = await api.get("/crm/templates"); setTemplates(data.data); } catch { setTemplates([]); }
  }, []);
  useEffect(() => { if (isCoach) loadTemplates(); }, [isCoach, loadTemplates]);

  const updateMember = (uid, patch) =>
    setData((d) => (d ? { ...d, members: d.members.map((m) => (m.user_id === uid ? { ...m, ...patch } : m)) } : d));

  if (!data) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  const members = data.members.filter((m) => !missingOnly || (!m.has_logged && (!isCoach || m.is_my_member)));
  return (
    <div data-testid="food-compliance-panel" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex flex-wrap gap-3">
          <span className="rounded-full border border-white/10 px-3 py-1 text-slate-300">{data.date}</span>
          <span data-testid="compliance-logged" className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-emerald-300">{t("food.logged")}: {data.logged}</span>
          <span data-testid="compliance-not-logged" className="rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-red-300">{t("food.notLogged")}: {data.not_logged}</span>
        </div>
        {isCoach && (
          <label className="flex items-center gap-2 cursor-pointer">
            <Switch data-testid="compliance-missing-toggle" checked={missingOnly} onCheckedChange={setMissingOnly} />
            <span className="text-xs text-slate-300">{t("crm.missingOnly")}</span>
          </label>
        )}
      </div>
      {members.map((m) => (
        <Card key={m.user_id} data-testid={`compliance-row-${m.user_id}`} className="bg-[#161923] border-white/5">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">{m.full_name || m.participant_code}{m.is_my_member ? " ★" : ""}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {m.has_logged ? `${m.entries_today} ${t("food.entriesToday")} · ${m.meals_logged.map((x) => t(`food.meal_${x}`)).join(", ")}` : t("food.notLoggedToday")}
              </div>
            </div>
            {isCoach && m.is_my_member && !m.has_logged && (
              <WhatsAppReminder testId={`compliance-wa-${m.user_id}`} context="FOOD_JOURNAL"
                memberName={m.full_name} whatsappNumber={m.whatsapp_number} memberUserId={m.user_id}
                lastRemindedAt={m.last_reminded_at} coachNote={m.coach_note}
                templates={templates} onTemplatesChanged={loadTemplates}
                onLogged={(ts) => updateMember(m.user_id, { last_reminded_at: ts })}
                onNoteSaved={(n) => updateMember(m.user_id, { coach_note: n })} />
            )}
            <span data-testid={`compliance-status-${m.user_id}`} className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${m.has_logged ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300" : "border-red-500/30 bg-red-500/15 text-red-300"}`}>
              {m.has_logged ? t("food.statusLogged") : t("food.statusMissing")}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DailySummary({ date }) {
  const { t } = useLang();
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = date ? `?date=${date}` : "";

  useEffect(() => { api.get(`/food-journal/summary/me${q}`).then(({ data }) => setSummary(data.data)).catch(() => {}); }, [q]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/food-journal/summary${q}`);
      setSummary(data.data);
      toast.success(t("food.summaryDone"));
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="food-daily-summary" className="bg-[#10121B] border-white/5">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-base md:text-lg font-semibold text-white">{t("food.dailySummary")}</h2>
          <Button data-testid="food-summary-btn" onClick={generate} disabled={busy} className="bg-white/10 hover:bg-white/15 text-white h-9">
            {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("food.summarizing")}</> : <><Sparkles className="h-4 w-4 mr-1" /> {t("food.makeSummary")}</>}
          </Button>
        </div>
        {summary ? (
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <span data-testid="food-summary-calories" className="font-[Outfit] text-2xl font-extrabold text-white">± {summary.total_calories} kkal</span>
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${VERDICT_STYLE[summary.verdict] || "border-white/10 text-slate-300"}`}>{t(`food.verdict_${summary.verdict}`)}</span>
              <span className="text-xs text-slate-500">{summary.meals_logged} {t("food.entriesToday")}</span>
            </div>
            <p className="text-slate-300">{summary.summary}</p>
            {summary.suggestions?.length > 0 && <ul className="text-xs text-slate-400 space-y-1">{summary.suggestions.map((s) => <li key={s}>• {s}</li>)}</ul>}
          </div>
        ) : <p className="text-sm text-slate-500">{t("food.noSummary")}</p>}
      </CardContent>
    </Card>
  );
}
